import { useState, useEffect } from "react";
import { usePageVisibility } from "react-page-visibility";
import { getDefinedLogInterval } from "../../../scripts/log";
import styled from "styled-components";
import zoneTypes from "../../live/zone/zoneTypes";
import LiveDecibelChart from "./LiveDecibelChart";
import theme from "../../../UI/theme";
import InputField from "../../UiComponents/InputField";

const Container = styled.div`
    height: 50px;
    width: 100%;
    overflow: hidden;
    grid-column: 1 / span 2;

    @media only screen and (max-width: ${theme.screenSizes.medium - 1}px) {
        height: 40px;
    }
    
    canvas {
        width: 100%;
        max-width: 100%;
        max-height: 100%;
    }
`

const MetricsContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 6px;
    row-gap: 5px;
    grid-column: 1 / span 2;
    width: 200px;
    align-items: end;
`

const InfoContainer = styled.div`
    grid-area: 3 / 1 / 4 / 4;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    width: 100%;
`

const MetricContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: space-between;
    justify-content: space-between;
    background-color: ${theme.colors.darkSpace80};
    padding: 2px 5px;
    box-sizing: border-box;
    border-radius: 2px;
    text-align: center;
    font-size: ${theme.fontSize.tiny};
`

const LabelContainer = styled.div`
    grid-area: 1 / 1 / 2 / 4;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Label = styled.label`
    font-size: ${theme.fontSize.tiny};
    color: ${theme.colors.textGray};
`

const NumberInput = styled(InputField).attrs({ type: "number" })`
  text-align: center;            /* optional: keep your text centered */
  appearance: textfield;         /* removes spinner + reserved space (most) */
  width: 50px;
  margin: 0 5px;
  padding: 2px 0;

  /* Chrome, Edge, Safari, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const LiveDecibelChartContainer = props => {
    const [graphData, setGraphData] = useState([]);
    const isVisible = usePageVisibility();
    const [averageDecibel, setAverageDecibel] = useState();
    const [maxDecibel, setMaxDecibel] = useState();
    const [minDecibel, setMinDecibel] = useState();

    useEffect(() => {
        if (isVisible === true) {
            getData();
        }
    }, [isVisible, props.dataInterval]);

    useEffect(() => {
        setGraphData(prev => {
            const prevCopy = JSON.parse(JSON.stringify(prev));
            prevCopy.pop();
            prevCopy.unshift({ time: props.zoneLive?.time, averageDecibel: props.zoneLive?.averageDecibel, noise: props.zoneLive?.noise, sysvol: props.zoneLive?.sysvol });
            computeMetrics(prevCopy);
            return prevCopy;
        });
    }, [props.zoneLive?.time]);

    const getData = async () => {
        var newData = await getDefinedLogInterval(props.zone?.customerId, props.zone?.zoneId, zoneTypes.intervals.LAST_15_MINUTES, null);
        computeMetrics(newData);
        setGraphData(newData);
    }

    const computeMetrics = (data) => {
        const filteredDataValues = data?.filter(dataPoint => dataPoint.averageDecibel > 0)?.map(dataPoint => dataPoint?.averageDecibel);
        setMaxDecibel(Math.max(...filteredDataValues));
        setMinDecibel(Math.min(...filteredDataValues));
        setAverageDecibel(filteredDataValues?.reduce((acc, num) => acc + num, 0) / filteredDataValues?.length);
    }

    return <>
        <Container>
            <LiveDecibelChart data={graphData} />
        </Container>
        <InfoContainer>
            <div>Loudness limit: 
                <NumberInput 
                    value={props.currentLimit}
                    onChange={(e) => props.onZoneChange(props.zone?.zoneId, e.target.value)}
                /> dB
            </div>
            <MetricsContainer>
                <LabelContainer>
                    <Label>Last 15 mins</Label>
                </LabelContainer>
                <MetricContainer>
                    <label>Avg</label>
                    <label>{averageDecibel?.toFixed(1)} dB</label>
                </MetricContainer>
                <MetricContainer>
                    <label>Max</label>
                    <label>{maxDecibel?.toFixed(1)} dB</label>
                </MetricContainer>
                <MetricContainer>
                    <label>Min</label>
                    <label>{minDecibel?.toFixed(1)} dB</label>
                </MetricContainer>
            </MetricsContainer>
        </InfoContainer>
    </>
}

export default LiveDecibelChartContainer;